import {
  DeliverableReports,
  MemoSubToggles,
} from '@pages/RunDeliverable/components/RunDeliverables/enums';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { companyEntityTransactions } from '@store/companyEntity/companyEntityThunk';
import { fetchBulkTransactionTitles } from '@pages/HedgeAccountingAdmin/store/hedgeAccountingSlice';

import {
  clearMemoTogglesThunk,
  clearReportsTogglesThunk,
  getEntityCurrency,
  toggleMemoToggleThunk,
  toggleReportTypeThunk,
  toggleTransactionSelection,
  fetchScheduledTransactions,
} from './runDeliverablesThunks';
import { RunDeliverablesState } from './types';

const initialState: RunDeliverablesState = {
  companyEntities: {},
  entityCurrency: [],
};

const runDeliverablesSlice = createSlice({
  name: 'runDeliverables',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(companyEntityTransactions.fulfilled, (state, action) => {
      const companyEntityId = action.payload?.data[0]?.comp_entity_id;

      state.companyEntities[companyEntityId] = {
        comp_entity_id: companyEntityId,
        reportToggles: {
          [DeliverableReports.Statement]: false,
          [DeliverableReports.Valuation]: false,
          [DeliverableReports.JournalEntries]: false,
          [DeliverableReports.Regression]: false,
          [DeliverableReports.JournalEntryAtMaturity]: false,
        },
        memoToggles: {
          [MemoSubToggles.Memo]: true,
          [MemoSubToggles.ActualsTermsheet]: true,
          [MemoSubToggles.HypotheticalsTermsheet]: true,
          [MemoSubToggles.AggregateNotional]: true,
          [MemoSubToggles.Effectiveness]: true,
        },
        transactions: action.payload.data.map((trade) => ({
          ...trade,
          selected: false,
        })),
      };
    });

    // Handle bulk transaction titles from new API - pending state
    builder.addCase(fetchBulkTransactionTitles.pending, (state, action) => {
      const entityId = action.meta.arg.company_entity_ids?.[0];
      if (entityId) {
        if (!state.companyEntities[entityId]) {
          // Create entity with loading state if it doesn't exist
          state.companyEntities[entityId] = {
            comp_entity_id: entityId,
            reportToggles: {
              [DeliverableReports.Statement]: false,
              [DeliverableReports.Valuation]: false,
              [DeliverableReports.JournalEntries]: false,
              [DeliverableReports.Regression]: false,
              [DeliverableReports.JournalEntryAtMaturity]: false,
            },
            memoToggles: {
              [MemoSubToggles.Memo]: true,
              [MemoSubToggles.ActualsTermsheet]: true,
              [MemoSubToggles.HypotheticalsTermsheet]: true,
              [MemoSubToggles.AggregateNotional]: true,
              [MemoSubToggles.Effectiveness]: true,
            },
            transactions: [],
            loading: true,
          };
        } else {
          state.companyEntities[entityId].loading = true;
        }
      }
    });

    // Handle bulk transaction titles from new API - fulfilled state
    builder.addCase(fetchBulkTransactionTitles.fulfilled, (state, action) => {
      const transactions = action.payload?.transaction_titles || [];
      const pagination = {
        page: action.payload?.page || 1,
        total_pages: action.payload?.total_pages || 1,
        total_count: action.payload?.total_transactions || 0,
        page_size: action.payload?.page_size || 20,
      };

      // Group transactions by company entity
      const transactionsByEntity: Record<string, any[]> = {};
      transactions.forEach((transaction: any) => {
        const entityId = transaction.company_entity?.id;
        if (entityId) {
          if (!transactionsByEntity[entityId]) {
            transactionsByEntity[entityId] = [];
          }
          // Map bulk API structure to expected format - keep nested structure for flattening
          transactionsByEntity[entityId].push({
            transaction_id: transaction.transaction_id,
            title: transaction.title || '',
            comp_entity_id: entityId,
            instrument_type: transaction.instrument_type,
            bank_entity: transaction.bank_entity,
            internal_details: transaction.internal_details,
            je_currency: transaction.je_currency,
            display_currency: transaction.display_currency,
            effective_date: transaction.effective_date,
            maturity_date: transaction.maturity_date,
            confirmation_ref_id: transaction.confirmation_ref_id,
            hypothetical: false,
            ifrs_component: false,
            selected: false,
          });
        }
      });

      // Update or create entity state for each entity with transactions
      Object.entries(transactionsByEntity).forEach(([entityId, entityTransactions]) => {
        if (!state.companyEntities[entityId]) {
          state.companyEntities[entityId] = {
            comp_entity_id: entityId,
            reportToggles: {
              [DeliverableReports.Statement]: false,
              [DeliverableReports.Valuation]: false,
              [DeliverableReports.JournalEntries]: false,
              [DeliverableReports.Regression]: false,
              [DeliverableReports.JournalEntryAtMaturity]: false,
            },
            memoToggles: {
              [MemoSubToggles.Memo]: true,
              [MemoSubToggles.ActualsTermsheet]: true,
              [MemoSubToggles.HypotheticalsTermsheet]: true,
              [MemoSubToggles.AggregateNotional]: true,
              [MemoSubToggles.Effectiveness]: true,
            },
            transactions: entityTransactions,
            pagination,
            loading: false,
          };
        } else {
          // Update existing entity with new transactions and pagination
          state.companyEntities[entityId].transactions = entityTransactions;
          state.companyEntities[entityId].pagination = pagination;
          state.companyEntities[entityId].loading = false;
        }
      });

      // Always stop loading for the requested entity, even when no transactions are returned
      const requestedEntityId = action.meta.arg.company_entity_ids?.[0];
      if (requestedEntityId && state.companyEntities[requestedEntityId]) {
        state.companyEntities[requestedEntityId].loading = false;
      }
    });

    // Handle bulk transaction titles from new API - rejected state
    builder.addCase(fetchBulkTransactionTitles.rejected, (state, action) => {
      const entityId = action.meta.arg.company_entity_ids?.[0];
      if (entityId && state.companyEntities[entityId]) {
        state.companyEntities[entityId].loading = false;
      }
    });
    builder.addCase(
      toggleReportTypeThunk.fulfilled,
      (
        state,
        action: PayloadAction<{
          companyEntityId: string | number;
          reportType: DeliverableReports;
        }>,
      ) => {
        const { companyEntityId, reportType } = action.payload;
        const entity = state.companyEntities[companyEntityId];
        if (entity) {
          entity.reportToggles[reportType] = !entity.reportToggles[reportType];
        }
      },
    );
    builder.addCase(
      toggleTransactionSelection.fulfilled,
      (
        state,
        action: PayloadAction<{
          companyEntityId: string | number;
          transactionId: string | number;
          selected: boolean;
        }>,
      ) => {
        const { companyEntityId, transactionId, selected } = action.payload;
        const entity = state.companyEntities[companyEntityId];
        if (entity) {
          const transaction = entity.transactions.find(
            (t) => t.transaction_id === transactionId,
          );
          if (transaction) {
            transaction.selected = selected;
          }
        }
      },
    );
    builder.addCase(
      toggleMemoToggleThunk.fulfilled,
      (
        state,
        action: PayloadAction<{
          companyEntityId: string;
          memoToggle: MemoSubToggles;
        }>,
      ) => {
        const { companyEntityId, memoToggle } = action.payload;
        const entity = state.companyEntities[companyEntityId];
        if (entity) {
          entity.memoToggles[memoToggle] = !entity.memoToggles[memoToggle];
        }
      },
    );
    builder.addCase(
      clearReportsTogglesThunk.fulfilled,
      (state, action: PayloadAction<{ companyEntityId: string }>) => {
        const { companyEntityId } = action.payload;
        const entity = state.companyEntities[companyEntityId];
        if (entity) {
          Object.keys(entity.reportToggles).forEach((key) => {
            entity.reportToggles[key as DeliverableReports] = false;
          });
        }
      },
    );
    builder.addCase(
      clearMemoTogglesThunk.fulfilled,
      (state, action: PayloadAction<{ companyEntityId: string }>) => {
        const { companyEntityId } = action.payload;
        const entity = state.companyEntities[companyEntityId];
        if (entity) {
          Object.keys(entity.memoToggles).forEach((key) => {
            entity.memoToggles[key as MemoSubToggles] = false;
          });
        }
      },
    );
    builder.addCase(getEntityCurrency.pending, (state) => {
      state.entityCurrency = [];
    });
    builder.addCase(
      getEntityCurrency.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.entityCurrency = action.payload;
      },
    );
    builder.addCase(
      fetchScheduledTransactions.fulfilled,
      (state, action: PayloadAction<{ success: boolean; data: Record<string, any[]> }>) => {
        const scheduledData = action.payload?.data || {};
        // Update each entity with its scheduled transaction IDs and deliverable details
        Object.entries(scheduledData).forEach(([entityId, transactions]) => {
          if (state.companyEntities[entityId]) {
            // Extract transaction IDs
            const transactionIds = transactions.map((t: any) => t.transaction_id);
            state.companyEntities[entityId].scheduledTransactionIds = transactionIds;
            // Store full deliverable details
            state.companyEntities[entityId].scheduledDeliverableDetails = transactions;
          }
        });
      },
    );
  },
});

export default runDeliverablesSlice.reducer;
